import { useState } from "react";
import { rankWordBoard, rankPersonBoard } from "@/mocks/homeData";

type TabKey = "word" | "person";

const tabs: { key: TabKey; label: string; hint: string }[] = [
  { key: "word", label: "热搜词榜", hint: "按关键词聚合" },
  { key: "person", label: "人物热度榜", hint: "按人物聚合" },
];

export default function RankBoard() {
  const [active, setActive] = useState<TabKey>("word");
  const list = active === "word" ? rankWordBoard : rankPersonBoard;

  return (
    <section id="rank" className="w-full bg-foreground-950 px-4 py-14 md:px-6 md:py-20">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-col gap-5 md:mb-10 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="mb-2 flex items-center gap-2 text-xs font-medium text-accent-400 md:text-sm">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-trophy-line text-base" aria-hidden="true"></i>
              </span>
              模块 02 · 热度榜
            </p>
            <h2 className="font-heading text-2xl font-bold text-background-50 md:text-4xl">
              实时热度榜单
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">
              热度由收录条目数、近期检索量与来源分布综合计算，每 30 分钟刷新一次，指数越高代表索引越活跃。
            </p>
          </div>

          <div className="flex w-fit items-center gap-1 rounded-full border border-background-50/15 bg-background-50/5 p-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActive(tab.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  active === tab.key
                    ? "bg-accent-500 text-foreground-950"
                    : "text-background-200 hover:text-background-50"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-x-6">
          {list.map((item, index) => (
            <a
              key={item.title}
              href="#latest"
              style={{ animationDelay: `${index * 50}ms` }}
              className="animate-fade-up group flex items-center gap-4 rounded-lg border border-background-50/10 bg-background-50/5 px-4 py-4 transition-colors hover:border-accent-500/60 hover:bg-background-50/10 md:px-5"
            >
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-heading text-base font-bold md:h-11 md:w-11 md:text-lg ${
                  item.rank <= 3
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50/10 text-background-200"
                }`}
              >
                {item.rank}
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-sm font-semibold text-background-50 md:text-base">{item.title}</p>
                  <span className="shrink-0 rounded-full bg-secondary-500/25 px-2 py-0.5 text-xs text-background-100">
                    {item.category}
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-background-50/10">
                    <div
                      className={`animate-bar h-full rounded-full ${
                        index % 3 === 0
                          ? "bg-primary-500"
                          : index % 3 === 1
                          ? "bg-accent-500"
                          : "bg-secondary-400"
                      }`}
                      style={{ width: `${item.heat}%` }}
                    ></div>
                  </div>
                  <span className="shrink-0 text-xs font-semibold text-background-100 md:text-sm">
                    {item.heat}
                  </span>
                </div>
              </div>

              <span
                className={`flex shrink-0 items-center gap-0.5 text-xs font-medium md:text-sm ${
                  item.trend === "up"
                    ? "text-accent-400"
                    : item.trend === "down"
                    ? "text-secondary-300"
                    : "text-background-400"
                }`}
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i
                    className={
                      item.trend === "up"
                        ? "ri-arrow-up-line"
                        : item.trend === "down"
                        ? "ri-arrow-down-line"
                        : "ri-subtract-line"
                    }
                    aria-hidden="true"
                  ></i>
                </span>
                {item.delta}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}